import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Plus, Trash2, FileText, Package } from "lucide-react";
import { format } from "date-fns";

const initialFormState = {
  document_number: '',
  document_date: format(new Date(), 'yyyy-MM-dd'),
  type: 'receipt',
  from_warehouse_id: '',
  to_warehouse_id: '',
  basis: '',
  notes: ''
};

const initialItemState = {
  item_id: '',
  quantity: 1
};

export default function InventoryMovementForm({ isOpen, onClose, onSubmit, warehouses, items: availableItems }) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState(initialFormState);
  const [formItems, setFormItems] = useState([initialItemState]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const nonBundleItems = availableItems.filter(item => item.type !== 'bundle');

  useEffect(() => {
    if (isOpen) {
      // Генерируем номер накладной
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      const hours = String(today.getHours()).padStart(2, '0');
      const minutes = String(today.getMinutes()).padStart(2, '0');
      
      setFormData(prev => ({
        ...initialFormState,
        document_number: `INV-${year}${month}${day}-${hours}${minutes}`
      }));
      setFormItems([initialItemState]);
    }
  }, [isOpen]);

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...formItems];
    newItems[index] = { ...newItems[index], [field]: value };
    setFormItems(newItems);
  };

  const addItem = () => {
    setFormItems([...formItems, { ...initialItemState }]);
  };

  const removeItem = (index) => {
    if (formItems.length > 1) {
      setFormItems(formItems.filter((_, i) => i !== index));
    }
  };

  const getItemDisplayName = (itemId) => {
    const item = nonBundleItems.find(i => i.id === itemId);
    if (!item) return t('inventory_movement.select_item');
    
    let displayName = item.name;
    
    // Добавляем специфичную информацию в зависимости от типа
    if (item.type === 'book_edition' && item.metadata?.isbn) {
      displayName += ` (ISBN: ${item.metadata.isbn})`;
    } else if (item.type === 'simple_product' && item.metadata?.gtin) {
      displayName += ` (GTIN: ${item.metadata.gtin})`;
    }
    return displayName;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Создаем данные для накладной и позиций
      const movementData = {
        movement: formData,
        items: formItems.map(item => ({
          item_id: item.item_id,
          quantity: item.quantity,
          movement_id: 'TEMP_ID' // Будет заменен на реальный ID после создания накладной
        }))
      };

      await onSubmit(movementData);
      
      // Сброс формы
      setFormData(initialFormState);
      setFormItems([initialItemState]);
    } catch (error) {
      console.error('Error creating inventory movement:', error);
    }
    
    setIsSubmitting(false);
  };

  const isTransferOrShipment = formData.type === 'transfer' || formData.type === 'shipment' || formData.type === 'write_off';
  const isReceiptOrTransfer = formData.type === 'receipt' || formData.type === 'transfer';
  
  // Determine which warehouse fields are required based on operation type
  const requiresFromWarehouse = formData.type === 'transfer' || formData.type === 'shipment' || formData.type === 'write_off';
  const requiresToWarehouse = formData.type === 'receipt' || formData.type === 'transfer';

  return (
            <Dialog open={isOpen} onOpenChange={onClose}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-white">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-slate-900 flex items-center gap-2">
                <FileText className="w-5 h-5" />
                {t('inventory_movement.title')}
              </DialogTitle>
              <DialogDescription>
                {t('inventory_movement.description')}
              </DialogDescription>
            </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <Card className="border-slate-200">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg flex items-center gap-2">
                <Package className="w-5 h-5 text-slate-600" />
                {t('inventory_movement.basic_info')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="document_number">{t('inventory_movement.document_number')}</Label>
                  <Input
                    id="document_number"
                    value={formData.document_number}
                    onChange={(e) => handleFieldChange('document_number', e.target.value)}
                    required
                  />
                </div>
                
                <div>
                  <Label htmlFor="document_date">{t('inventory_movement.document_date')}</Label>
                  <Input
                    id="document_date"
                    type="date"
                    value={formData.document_date}
                    onChange={(e) => handleFieldChange('document_date', e.target.value)}
                    required
                  />
                </div>
                
                <div>
                  <Label htmlFor="type">{t('inventory_movement.operation_type')}</Label>
                  <Select value={formData.type} onValueChange={(value) => handleFieldChange('type', value)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="receipt">{t('inventory_movement.operation_types.receipt')}</SelectItem>
                      <SelectItem value="transfer">{t('inventory_movement.operation_types.transfer')}</SelectItem>
                      <SelectItem value="shipment">{t('inventory_movement.operation_types.shipment')}</SelectItem>
                      <SelectItem value="write_off">{t('inventory_movement.operation_types.write_off')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {requiresFromWarehouse && (
                  <div>
                    <Label htmlFor="from_warehouse">{t('inventory_movement.from_warehouse')}</Label>
                    <Select value={formData.from_warehouse_id} onValueChange={(value) => handleFieldChange('from_warehouse_id', value)} required>
                      <SelectTrigger>
                        <SelectValue placeholder={t('inventory_movement.select_warehouse')} />
                      </SelectTrigger>
                      <SelectContent>
                        {warehouses.filter(w => w.is_active).map((warehouse) => (
                          <SelectItem key={warehouse.id} value={warehouse.id}>
                            {warehouse.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                
                {requiresToWarehouse && (
                  <div>
                    <Label htmlFor="to_warehouse">{t('inventory_movement.to_warehouse')}</Label>
                    <Select value={formData.to_warehouse_id} onValueChange={(value) => handleFieldChange('to_warehouse_id', value)} required>
                      <SelectTrigger>
                        <SelectValue placeholder={t('inventory_movement.select_warehouse')} />
                      </SelectTrigger>
                      <SelectContent>
                        {warehouses.filter(w => w.is_active).map((warehouse) => (
                          <SelectItem key={warehouse.id} value={warehouse.id}>
                            {warehouse.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              <div>
                <Label htmlFor="basis">{t('inventory_movement.basis')}</Label>
                <Input
                  id="basis"
                  value={formData.basis}
                  onChange={(e) => handleFieldChange('basis', e.target.value)}
                  placeholder={t('inventory_movement.basis_placeholder')}
                />
              </div>
              
              <div>
                <Label htmlFor="notes">{t('inventory_movement.notes')}</Label>
                <Textarea
                  id="notes"
                  placeholder={t('inventory_movement.notes_placeholder')}
                  value={formData.notes}
                  onChange={(e) => handleFieldChange('notes', e.target.value)}
                  className="h-20"
                />
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg flex items-center justify-between">
                <span>{t('inventory_movement.invoice_items')}</span>
                <Button type="button" onClick={addItem} variant="outline" size="sm">
                  <Plus className="w-4 h-4 mr-2" />
                  {t('inventory_movement.add_item')}
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {formItems.map((item, index) => (
                <div key={index} className="grid grid-cols-12 gap-4 items-end p-4 border border-slate-200 rounded-lg">
                  <div className="col-span-8">
                    <Label>{t('inventory_movement.item')}</Label>
                    <Select 
                      value={item.item_id} 
                      onValueChange={(value) => handleItemChange(index, 'item_id', value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t('inventory_movement.select_item')} />
                      </SelectTrigger>
                      <SelectContent>
                        {nonBundleItems.map((itemOption) => (
                          <SelectItem key={itemOption.id} value={itemOption.id}>
                            {getItemDisplayName(itemOption.id)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="col-span-3">
                    <Label>{t('inventory_movement.quantity')}</Label>
                    <Input
                      type="number"
                      min="1"
                      value={item.quantity}
                      onChange={(e) => handleItemChange(index, 'quantity', parseInt(e.target.value) || 1)}
                    />
                  </div>
                  
                  <div className="col-span-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeItem(index)}
                      disabled={formItems.length === 1}
                      className="text-red-500 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
              
              {formItems.length === 0 && (
                <div className="text-center py-8 text-slate-500">
                  <p>{t('inventory_movement.add_items_to_invoice')}</p>
                </div>
              )}
            </CardContent>
          </Card>

          <div className="flex justify-end gap-3 pt-6 border-t">
            <Button type="button" variant="outline" onClick={onClose}>
              {t('inventory_movement.cancel')}
            </Button>
            <Button 
              type="submit" 
              disabled={
                isSubmitting || 
                formItems.length === 0 || 
                formItems.some(item => !item.item_id) ||
                (requiresFromWarehouse && !formData.from_warehouse_id) ||
                (requiresToWarehouse && !formData.to_warehouse_id)
              }
              className="bg-amber-600 hover:bg-amber-700"
            >
              {isSubmitting ? t('inventory_movement.creating') : t('inventory_movement.create_invoice')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
